/**
* Copyright by: 

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/

import {
  getLastPositionText,
  getPositionText,
  getDateText,
  getYearlyDescription
} from '../app/shared/functions';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { merge, Observable, Subject, throwError } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { DayOfWeek, FrequencyEnum, ScheduledTaskDto } from '../models/dtos/scheduled-task.dto';
import { RecurringModel, ScheduledTaskModel } from '../models/scheduled-task.model';
import { ApiService } from './api.service';
import { SnackbarService } from './snackbar.service';

export enum RecurringConfig {
  withRepeat,
  standalone
}
export interface weekdayModel {
  readonly key: string;
  readonly value: string;
  readonly dayIndex: number;
}
@Injectable({
  providedIn: 'root'
})
export class ScheduledTaskService {
  readonly weekdays: readonly weekdayModel[] = [
    { key: DayOfWeek.MONDAY, value: $localize`:Mon.@@mondayShortName:Mon.`, dayIndex: 1 },
    { key: DayOfWeek.TUESDAY, value: $localize`:Tue.@@tuesdayShortName:Tue.`, dayIndex: 2 },
    { key: DayOfWeek.WEDNESDAY, value: $localize`:Wed.@@wednesdayShortName:Wed.`, dayIndex: 3 },
    { key: DayOfWeek.THURSDAY, value: $localize`:Thu.@@thursdayShortName:Thu.`, dayIndex: 4 },
    { key: DayOfWeek.FRIDAY, value: $localize`:Fri.@@fridayShortName:Fri.`, dayIndex: 5 },
    { key: DayOfWeek.SATURDAY, value: $localize`:Sat.@@saturdayShortName:Sat.`, dayIndex: 6 },
    { key: DayOfWeek.SUNDAY, value: $localize`:Sun.@@sundayShortName:Sun.`, dayIndex: 0 }
  ];

  constructor(
    private apiService: ApiService,
    private snackbarService: SnackbarService,
    @Inject(LOCALE_ID) public locale: string
  ) {}

  deleteScheduleTaskEvent = new Subject<ScheduledTaskModel>();
  addScheduleTaskEvent = new Subject<ScheduledTaskModel>();
  scheduledTaskSelectionChanged = new Subject<ScheduledTaskModel | undefined>();

  private scheduledTaskDeleted$ = this.deleteScheduleTaskEvent.pipe(
    switchMap((scheduledTask) => this.deleteScheduledTask(scheduledTask.taskId))
  );

  private scheduledTaskAdded$ = this.addScheduleTaskEvent
    .pipe
    // switchMap((scheduledTask) => this.deleteScheduledTask(scheduledTask.taskId))
    ();

  private scheduledTaskEvent$ = merge(this.scheduledTaskDeleted$, this.scheduledTaskAdded$);

  private scheduledTasks$ = this.scheduledTaskEvent$.pipe(
    startWith([]),
    switchMap(() => this.getScheduledTasks())
  );

  // filter out expired scheduled tasks
  filteredScheduledTasks$ = this.scheduledTasks$.pipe(
    map((tasks) =>
      tasks.filter(
        (task) => Date.parse(task.scheduledAt) > Date.now() || task.recurring !== undefined
      )
    )
  );

  private getScheduledTasks(): Observable<ScheduledTaskModel[]> {
    return this.apiService.get('scheduled-task').pipe(
      map((scheduledTaskDtos: ScheduledTaskDto[]) => {
        let scheduledTasks: ScheduledTaskModel[] = [];
        let scheduledTaskModel = Object.create(ScheduledTaskModel.prototype) as ScheduledTaskModel;
        for (var scheduledTaskDto of scheduledTaskDtos) {
          scheduledTasks.push(scheduledTaskModel.fromDto(scheduledTaskDto));
        }
        scheduledTasks.sort((a, b) => {
          return new Date(a.scheduledAt).valueOf() - new Date(b.scheduledAt).valueOf();
        });
        return scheduledTasks;
      }),
      catchError((error: HttpErrorResponse) =>
        this.handleError(error, `Error occured while fetching scheduled-tasks`)
      )
    );
  }

  getScheduledTask(id: String): Observable<ScheduledTaskModel> {
    return this.apiService.get(`scheduled-task/${id}`).pipe(
      map((scheduledTaskDto: ScheduledTaskDto) => {
        let scheduledTaskModel = Object.create(ScheduledTaskModel.prototype) as ScheduledTaskModel;
        return scheduledTaskModel.fromDto(scheduledTaskDto);
      }),
      catchError((error: HttpErrorResponse) =>
        this.handleError(error, `Error occured while fetching scheduled-task ${id}`)
      )
    );
  }

  deleteScheduledTask(scheduledTaskId: string): Observable<ScheduledTaskModel> {
    return this.apiService.delete(`scheduled-task/${scheduledTaskId}`).pipe(
      map((scheduledTaskDto: ScheduledTaskDto) => {
        let scheduledTaskModel = Object.create(ScheduledTaskModel.prototype) as ScheduledTaskModel;
        const deletedScheduled = scheduledTaskModel.fromDto(scheduledTaskDto);
        this.scheduledTaskSelectionChanged.next(undefined);
        return deletedScheduled;
      }),
      catchError((error: HttpErrorResponse) =>
        this.handleError(error, `Error occured while deleting scheduled-task ${scheduledTaskId}`)
      )
    );
  }

  handleError(error: HttpErrorResponse, message: string): Observable<never> {
    this.snackbarService.showSnackBar(message, '', 'error');
    return throwError(() => message);
  }

  getRecurringConfig(
    scheduledTaskRecurring: RecurringModel,
    recurringConfig: RecurringConfig,
    scheduledAt: string
  ): string {
    switch (scheduledTaskRecurring.frequency) {
      case FrequencyEnum.DAILY:
        if (scheduledTaskRecurring.options?.every && scheduledTaskRecurring.options?.every > 1) {
          if (recurringConfig === RecurringConfig.withRepeat) {
            return $localize`:Repeat every selected days@@repeatEverySelectedDays:Repeat every ${scheduledTaskRecurring.options?.every} days`;
          } else {
            return $localize`:Every selected days@@everySelectedDays:Every ${scheduledTaskRecurring.options?.every} days`;
          }
        } else if (recurringConfig === RecurringConfig.withRepeat) {
          return $localize`:Repeat every day@@repeatEveryDay:Repeat every day`;
        } else {
          return $localize`:Every day@@everyDay:Every day`;
        }

      case FrequencyEnum.WEEKLY:
        if (scheduledTaskRecurring.options?.every && scheduledTaskRecurring.options?.every > 1) {
          let weekdaysString: string = this.getWeekDaysString(scheduledTaskRecurring);

          if (recurringConfig === RecurringConfig.withRepeat) {
            return $localize`:Repeat every selected number of weeks@@repeatEverySelectedNumberOfWeeksConfig:Repeat every ${scheduledTaskRecurring.options?.every} weeks (${weekdaysString})`;
          } else {
            return $localize`:Every selected number of weeks@@everySelectedNumberOfWeeksConfig:Every ${scheduledTaskRecurring.options?.every} weeks (${weekdaysString})`;
          }
        }
        //weekdaily
        if (
          scheduledTaskRecurring.options?.execution_days?.length == 5 &&
          !scheduledTaskRecurring.options?.execution_days?.includes(DayOfWeek.SATURDAY) &&
          !scheduledTaskRecurring.options?.execution_days?.includes(DayOfWeek.SUNDAY)
        ) {
          if (recurringConfig === RecurringConfig.withRepeat) {
            return $localize`:Repeat every weekday (Mon. - Fri.)@@repeatEveryWeekday:Repeat every weekday (Mon. - Fri.)`;
          } else {
            return $localize`:Every weekday (Mon. - Fri.)@@everyWeekdayConfig:Every weekday (Mon. - Fri.)`;
          }
        } else {
          //weekly
          let weekdaysString: string = this.getWeekDaysString(scheduledTaskRecurring);

          if (recurringConfig === RecurringConfig.withRepeat) {
            return $localize`:Repeat every week@@repeatEveryWeekConfig:Repeat every week (${weekdaysString})`;
          } else {
            return $localize`:Every week@@everyWeekConfig:Every week (${weekdaysString})`;
          }
        }

      case FrequencyEnum.MONTHLY:
        let repeatText = '';
        if (
          scheduledTaskRecurring.options?.execution_day_position &&
          scheduledTaskRecurring.options.execution_days
        ) {
          if (scheduledTaskRecurring.options?.execution_day_position == 'LAST') {
            repeatText = getLastPositionText(scheduledAt, this.locale);
          } else {
            repeatText = getPositionText(scheduledAt, this.locale);
          }
        } else {
          repeatText = getDateText(scheduledAt)?.trim();
        }
        if (scheduledTaskRecurring.options?.every && scheduledTaskRecurring.options?.every > 1) {
          if (recurringConfig === RecurringConfig.withRepeat) {
            return $localize`:Repeat every selected number of months@@repeatEverySelectedNumberOfMonths:Repeat every ${scheduledTaskRecurring.options?.every} months (${repeatText} of month)`;
          } else {
            return $localize`:Every selected number of month@@everySelectedNumberOfMonths:Every ${scheduledTaskRecurring.options?.every} months (${repeatText} of month)`;
          }
        } else {
          if (recurringConfig === RecurringConfig.withRepeat) {
            return $localize`:Repeat every month@@repeatEveryMonth:Repeat every month (${repeatText} of month)`;
          } else {
            return $localize`:Every month@@everyMonth:Every month (${repeatText} of month)`;
          }
        }

      case FrequencyEnum.YEARLY:
        const scheduleDate = new Date(scheduledAt);
        if (scheduledTaskRecurring.options?.every && scheduledTaskRecurring.options?.every > 1) {
          if (recurringConfig === RecurringConfig.withRepeat) {
            return $localize`:Repeat every selected number of years@@repeatEverySelectedNumberOfYears:Repeat every ${
              scheduledTaskRecurring.options?.every
            } years (${getYearlyDescription(scheduleDate, this.locale)}of year)`;
          } else {
            return $localize`:Every selected number of years@@everySelectedNumberOfYears:Every ${
              scheduledTaskRecurring.options?.every
            } years (${getYearlyDescription(scheduleDate, this.locale)}of year)`;
          }
        } else {
          if (recurringConfig === RecurringConfig.withRepeat) {
            return $localize`:Repeat every year@@repeatEveryYearConfig:Repeat every year (${getYearlyDescription(
              scheduleDate,
              this.locale
            )}of year)`;
          } else {
            return $localize`:Every year@@everyYearConfig:Every year (${getYearlyDescription(
              scheduleDate,
              this.locale
            )}of year)`;
          }
        }

      default:
        return '';
    }
  }

  private getWeekDaysString(scheduledTaskRecurring: RecurringModel) {
    const weekDaysShortNames: string[] = [];
    let weekdaysString: string = '';
    if (scheduledTaskRecurring.options?.execution_days) {
      this.weekdays.forEach((element) => {
        const dayName = scheduledTaskRecurring.options?.execution_days?.find(
          (day) => day === element.key
        );
        const dayShortName = this.weekdays.find((day) => day.key === dayName)?.value;
        if (dayShortName) weekDaysShortNames.push(dayShortName);
      });
      weekdaysString += weekDaysShortNames.join(', ');
    }
    return weekdaysString;
  }
}
